import { useState, useMemo } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
	getAllUsersOptions,
	deleteUserMutation,
	updateUserRoleMutation,
	sendPasswordResetEmailMutation,
	getAllUsersQueryKey,
} from "@/lib/client/@tanstack/react-query.gen";
import type { UserDto } from "@/lib/client/types.gen";
import type { ColumnDef } from "@tanstack/react-table";
import {
	DataTable,
	DataTableColumnHeader,
	DataTableToolbar,
	DataTableFilterText,
	DataTableFilterFaceted,
} from "@/components/data-table";
import { normalizeString } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { User, Trash2, Shield, Mail, Loader } from "lucide-react";
import { Badge } from "@/components/ui/badge";

export const Route = createFileRoute("/admin/configuration/users")({
	component: UsersManagementPage,
});

const getRoleBadgeColor = (role?: string) => {
	switch (role) {
		case "ADMIN":
			return "bg-purple-100 text-purple-800";
		case "PUBLIC":
			return "bg-blue-100 text-blue-800";
		default:
			return "bg-gray-100 text-gray-800";
	}
};

export default function UsersManagementPage() {
	const queryClient = useQueryClient();
	const [roleDialogOpen, setRoleDialogOpen] = useState(false);
	const [selectedUser, setSelectedUser] = useState<UserDto | null>(null);
	const [selectedRole, setSelectedRole] = useState<string>("");

	const { data: users, isLoading } = useQuery(getAllUsersOptions());

	const deleteUserMut = useMutation({
		...deleteUserMutation(),
		onSuccess: () => {
			toast.success("Utilisateur supprimé avec succès");
			queryClient.invalidateQueries({ queryKey: getAllUsersQueryKey() });
		},
		onError: () => {
			toast.error("Erreur lors de la suppression de l'utilisateur");
		},
	});

	const updateRoleMut = useMutation({
		...updateUserRoleMutation(),
		onSuccess: () => {
			toast.success("Rôle modifié avec succès");
			queryClient.invalidateQueries({ queryKey: getAllUsersQueryKey() });
			setRoleDialogOpen(false);
			setSelectedUser(null);
			setSelectedRole("");
		},
		onError: () => {
			toast.error("Erreur lors de la modification du rôle");
		},
	});

	const sendPasswordResetMut = useMutation({
		...sendPasswordResetEmailMutation(),
		onSuccess: () => {
			toast.success("Email de réinitialisation envoyé avec succès");
		},
		onError: () => {
			toast.error("Erreur lors de l'envoi de l'email");
		},
	});

	const handleDeleteUser = (user: UserDto) => {
		if (
			confirm(
				`Êtes-vous sûr de vouloir supprimer l'utilisateur ${user.firstName} ${user.lastName} (${user.email}) ?`,
			)
		) {
			deleteUserMut.mutate({ path: { id: user.id } });
		}
	};

	const handleOpenRoleDialog = (user: UserDto) => {
		setSelectedUser(user);
		setSelectedRole(user.role || "");
		setRoleDialogOpen(true);
	};

	const handleUpdateRole = () => {
		if (!selectedUser || !selectedRole) {
			toast.error("Veuillez sélectionner un rôle");
			return;
		}

		updateRoleMut.mutate({
			path: { id: selectedUser.id },
			body: { role: selectedRole as any },
		});
	};

	const handleSendPasswordReset = (user: UserDto) => {
		if (
			confirm(
				`Envoyer un email de réinitialisation de mot de passe à ${user.email} ?`,
			)
		) {
			sendPasswordResetMut.mutate({ path: { id: user.id } });
		}
	};

	const columns = useMemo<ColumnDef<UserDto>[]>(
		() => [
			{
				id: "user",
				accessorFn: (row) => row.lastName + " " + row.firstName,
				header: ({ column }) => (
					<DataTableColumnHeader column={column} title="Utilisateur" />
				),
				meta: { label: "Utilisateur" },
				cell: ({ row }) => (
					<div className="flex items-center gap-2">
						<User className="h-4 w-4 text-gray-400" />
						<div>
							<p className="font-medium">
								{row.original.firstName} {row.original.lastName}
							</p>
						</div>
					</div>
				),
				filterFn: (row, columnId, filterValue: string) => {
					const searchValue = normalizeString(filterValue);
					const fullName = normalizeString(
						`${row.original.firstName} ${row.original.lastName}`
					);
					const reverseName = normalizeString(
						`${row.original.lastName} ${row.original.firstName}`
					);
					const email = normalizeString(row.original.email || "");
					return (
						fullName.includes(searchValue) ||
						reverseName.includes(searchValue) ||
						email.includes(searchValue)
					);
				},
				sortDescFirst: false,
			},
			{
				accessorKey: "email",
				header: ({ column }) => (
					<DataTableColumnHeader column={column} title="Email" />
				),
				meta: { label: "Email" },
				cell: ({ row }) => (
					<p className="text-sm text-gray-600">{row.original.email}</p>
				),
			},
			{
				accessorKey: "role",
				header: ({ column }) => (
					<DataTableColumnHeader column={column} title="Rôle" />
				),
				meta: {
					label: "Rôle",
					textValue: (row: UserDto) => {
						const labels: Record<string, string> = { ADMIN: "Administrateur", PUBLIC: "Public" };
						return labels[row.role || ""] || row.role || "";
					},
				},
				filterFn: (row, id, value: string[]) => {
					return value.includes(row.getValue(id));
				},
				cell: ({ row }) => (
					<Badge
						variant="secondary"
						className={getRoleBadgeColor(row.original.role)}
					>
						{row.original.role}
					</Badge>
				),
			},
			{
				id: "enabled",
				accessorFn: (row) => (row.enabled ? "true" : "false"),
				header: ({ column }) => (
					<DataTableColumnHeader column={column} title="Statut" />
				),
				meta: {
					label: "Statut",
					textValue: (row: UserDto) => row.enabled ? "Actif" : "Inactif",
				},
				filterFn: (row, id, value: string[]) => {
					return value.includes(String(row.getValue(id)));
				},
				cell: ({ row }) => {
					const enabled = row.original.enabled;
					return (
						<Badge
							variant={enabled ? "default" : "secondary"}
							className={
								enabled
									? "bg-green-100 text-green-800"
									: "bg-red-100 text-red-800"
							}
						>
							{enabled ? "Actif" : "Inactif"}
						</Badge>
					);
				},
			},
			{
				id: "actions",
				header: "Actions",
				meta: { label: "Actions" },
				enableSorting: false,
				cell: ({ row }) => {
					const user = row.original;
					return (
						<div className="flex items-center gap-2">
							<Button
								size="sm"
								variant="outline"
								onClick={() => handleOpenRoleDialog(user)}
								title="Changer le rôle"
							>
								<Shield className="h-3 w-3 mr-1" />
								Rôle
							</Button>
							<Button
								size="sm"
								variant="outline"
								onClick={() => handleSendPasswordReset(user)}
								disabled={sendPasswordResetMut.isPending}
								title="Envoyer un email de réinitialisation"
							>
								<Mail className="h-3 w-3 mr-1" />
								Reset MDP
							</Button>
							<Button
								size="sm"
								variant="ghost"
								onClick={() => handleDeleteUser(user)}
								disabled={deleteUserMut.isPending}
								title="Supprimer l'utilisateur"
							>
								<Trash2 className="h-3 w-3" />
							</Button>
						</div>
					);
				},
			},
		],
		[deleteUserMut.isPending, sendPasswordResetMut.isPending],
	);

	if (isLoading) {
		return (
			<div className="flex justify-center items-center h-96">
				<Loader className="h-8 w-8 animate-spin text-blue-600" />
			</div>
		);
	}

	return (
		<div className="p-6">
			<div className="mb-6">
				<h1 className="text-2xl font-bold text-gray-900">
					Gestion des utilisateurs
				</h1>
				<p className="text-gray-600 mt-2">
					Gérez les utilisateurs de la plateforme
				</p>
			</div>

			<DataTable
				columns={columns}
				data={users || []}
				emptyMessage="Aucun utilisateur"
				toolbar={(table) => (
					<DataTableToolbar table={table} showExportExcel exportFilename="utilisateurs">
						{table.getColumn("email") && (
							<DataTableFilterText
								column={table.getColumn("email")!}
								placeholder="Rechercher par email..."
							/>
						)}
						{table.getColumn("role") && (
							<DataTableFilterFaceted
								column={table.getColumn("role")!}
								title="Rôle"
								options={[
									{ label: "ADMIN", value: "ADMIN" },
									{ label: "PUBLIC", value: "PUBLIC" },
								]}
							/>
						)}
						{table.getColumn("enabled") && (
							<DataTableFilterFaceted
								column={table.getColumn("enabled")!}
								title="Statut"
								options={[
									{ label: "Actif", value: "true" },
									{ label: "Inactif", value: "false" },
								]}
							/>
						)}
					</DataTableToolbar>
				)}
			/>

			<Dialog open={roleDialogOpen} onOpenChange={setRoleDialogOpen}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>Modifier le rôle</DialogTitle>
						<DialogDescription>
							Changer le rôle de {selectedUser?.firstName}{" "}
							{selectedUser?.lastName}
						</DialogDescription>
					</DialogHeader>
					<div className="space-y-4 py-4">
						<div>
							<Label htmlFor="role">Rôle</Label>
							<Select value={selectedRole} onValueChange={setSelectedRole}>
								<SelectTrigger>
									<SelectValue placeholder="Sélectionnez un rôle" />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value="ADMIN">ADMIN</SelectItem>
									<SelectItem value="PUBLIC">PUBLIC</SelectItem>
								</SelectContent>
							</Select>
						</div>
					</div>
					<DialogFooter>
						<Button
							variant="outline"
							onClick={() => {
								setRoleDialogOpen(false);
								setSelectedUser(null);
								setSelectedRole("");
							}}
						>
							Annuler
						</Button>
						<Button
							onClick={handleUpdateRole}
							disabled={updateRoleMut.isPending}
						>
							{updateRoleMut.isPending ? "Modification..." : "Modifier"}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</div>
	);
}
